// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import {
  Button,
  Fade,
  CircularProgress,
  SnackbarContent,
  Collapse,
  Typography,
} from '@mui/material';
import { Box, useTheme } from '@mui/system';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { FORM_ERROR } from 'final-form';
import { TextField, Validators } from 'Components/forms';
import { Form } from 'react-final-form';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { PasswordUpdateThunk } from '../accountThunk';
import stylesDef from './styles';

interface PasswordFormValues {
  oldPassword: string;
  password: string;
  passwordConfirm: string;
}

const NOT_CHANGED = 'Your password was not changed.';
const CHANGED = 'Your password was changed.';

// the words of a change that did not happen: always that it did not, then the server's reason when it gave one
const notChangedText = (reason?: string) => (reason ? `${NOT_CHANGED} ${reason}` : NOT_CHANGED);

export const AccountPassword = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);

  const dispatch = useAppDispatch();
  const { updatePasswordMessage } = useAppSelector(
    (state) => state.account,
  );

  // the form succeeds only when the server changed the password; anything else is the form's error
  const onFormSubmit = async ({ oldPassword, password }: PasswordFormValues) => {
    try {
      const result = await dispatch(PasswordUpdateThunk({ oldPassword, password }));
      return result.success ? undefined : { [FORM_ERROR]: notChangedText(result.message) };
    } catch {
      return { [FORM_ERROR]: notChangedText() };
    }
  };

  return (
    <Box sx={styles.contentWrapper}>
      <Form
        onSubmit={onFormSubmit}
        render={({ hasSubmitErrors, modifiedSinceLastSubmit, submitting, pristine, submitError, submitSucceeded, handleSubmit }) => (
          <Box
            component="form"
            sx={styles.form}
            onSubmit={handleSubmit}
          >
            <Typography variant="h4">RESET PASSWORD</Typography>
            <TextField
              name="oldPassword"
              label="Current Password"
              type="password"
              validate={Validators.composeValidators(Validators.required, Validators.minLength7)}
            />
            <TextField
              name="password"
              label="New Password"
              type="password"
              validate={Validators.composeValidators(Validators.required, Validators.strongPassword)}
            />
            <TextField
              name="passwordConfirm"
              label="Confirm Password"
              type="password"
              validate={Validators.composeValidators(Validators.required, Validators.strongPassword, Validators.passwordsMatch)}
            />
            <Collapse in={(hasSubmitErrors && !modifiedSinceLastSubmit)}>
              <SnackbarContent
                sx={styles.errorSnackbar}
                message={(
                  <Box sx={styles.snackbarContent}>
                    <ErrorIcon />
                    {submitError}
                  </Box>
                )}
              />
            </Collapse>
            <Collapse in={!submitting && submitSucceeded}>
              <SnackbarContent
                sx={styles.successSnackbar}
                message={(
                  <Box sx={styles.snackbarContent}>
                    <CheckCircleIcon />
                    {updatePasswordMessage || CHANGED}
                  </Box>
                )}
              />
            </Collapse>
            <Button
              variant="outlined"
              color="primary"
              sx={styles.submitButton}
              type="submit"
              disabled={pristine || (hasSubmitErrors && !modifiedSinceLastSubmit) || submitting}
            >
              <Fade in={submitting}>
                <CircularProgress size={24} sx={styles.buttonProgress} />
              </Fade>
              <Fade in={!submitting}>
                <span>Update Password</span>
              </Fade>
            </Button>
          </Box>
        )}
      />
      <Box sx={styles.requirements}>
        <Typography sx={styles.rheader} variant="h5">
          Your new password must contain:
        </Typography>
        <Typography sx={styles.rdetails}>A minimum of 8 characters</Typography>
        <Typography sx={styles.rdetails}>A number</Typography>
        <Typography sx={styles.rdetails}>A lowercase letter and an uppercase letter</Typography>
        <Typography sx={styles.rdetails}>
          A special character: ~`!@#$%^&*()_-+=
          {'{'}
          [
          {'}'}
          ]|\:;
          &quot;&apos;
          {'<'}
          ,
          {'>'}
          .?/
        </Typography>
      </Box>
    </Box>
  );
};

export default AccountPassword;
